<template>
  <v-app>
    <!-- Header -->
    <v-app-bar app color="white" elevation="1" height="64">
      <!-- Logo + brand name -->
      <router-link to="/fs-dashboard" class="d-flex align-center text-decoration-none mr-0 mr-sm-2" style="gap:8px;">
        <div class="fs-logo-circle">FS</div>
        <span class="hidden-xs-only" style="font-size:18px; font-weight:700; color:#1A3A5C; letter-spacing:-0.3px;">The First Steps</span>
      </router-link>

      <v-spacer />

      <!-- Desktop nav — icon + label, underline indicator -->
      <div class="hidden-sm-and-down d-flex align-center">
        <router-link
          v-for="item in navItems"
          :key="item.to"
          :to="item.to"
          class="fs-nav-link"
          :class="{ 'fs-nav-link--active': isActive(item.to) }"
        >
          <v-icon size="20">{{ item.icon }}</v-icon>
          <span class="fs-nav-label">{{ item.label }}</span>
          <span class="fs-nav-underline" :class="{ 'fs-nav-underline--active': isActive(item.to) }" />
        </router-link>
      </div>

      <!-- Mobile hamburger -->
      <v-app-bar-nav-icon class="hidden-md-and-up" @click="drawer = !drawer" color="#1A3A5C" />
    </v-app-bar>

    <!-- Mobile drawer -->
    <v-navigation-drawer v-model="drawer" app temporary right>
      <v-list nav dense class="mt-2">
        <v-list-item
          v-for="item in navItems"
          :key="item.to"
          :to="item.to"
          active-class="fs-drawer-active"
          @click="drawer = false"
        >
          <v-list-item-icon><v-icon color="#1A3A5C">{{ item.icon }}</v-icon></v-list-item-icon>
          <v-list-item-title style="color:#1A3A5C">{{ item.label }}</v-list-item-title>
        </v-list-item>
        <v-divider class="my-2" />
        <v-list-item @click="signOut">
          <v-list-item-icon><v-icon color="#1A3A5C">mdi-logout</v-icon></v-list-item-icon>
          <v-list-item-title style="color:#1A3A5C">Sign out</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-navigation-drawer>

    <!-- Grace period banner -->
    <v-banner
      v-if="inGrace"
      color="orange darken-1"
      dark
      single-line
      sticky
      style="z-index:5;"
    >
      <v-icon slot="icon" color="white">mdi-alert-circle-outline</v-icon>
      Your card was declined — please update your payment details to keep your account.
      <template v-slot:actions>
        <v-btn text small white @click="openPortal" :loading="portalLoading">Update payment</v-btn>
      </template>
    </v-banner>

    <!-- Page content -->
    <v-main style="background:#E8EEF8; min-height:100vh;">
      <slot />
    </v-main>

    <!-- Footer -->
    <v-footer color="#1A3A5C" dark padless>
      <v-container style="max-width:1400px;">
        <v-row align="center" class="py-4">
          <v-col cols="12" sm="9">
            <span class="caption white--text">
              <strong>The First Steps</strong> is an information-organising tool. It does not provide legal advice and does not replace a solicitor.
            </span>
          </v-col>
          <v-col cols="12" sm="3" class="text-sm-right">
            <router-link to="/fs-glossary" style="color:#2E75B6; text-decoration:underline; font-size:13px;">Glossary</router-link>
          </v-col>
        </v-row>
      </v-container>
    </v-footer>
  </v-app>
</template>

<script>
export default {
  name: 'FirstStepsLayout',
  data: () => ({
    drawer: false,
    inGrace: false,
    portalLoading: false,
    navItems: [
      { to: '/fs-dashboard', label: 'Dashboard', icon: 'mdi-view-dashboard-outline' },
      { to: '/fs-glossary',  label: 'Glossary',  icon: 'mdi-book-open-outline' },
      { to: '/fs-share',     label: 'Share',     icon: 'mdi-share-variant-outline' },
      { to: '/fs-account',   label: 'Account',   icon: 'mdi-account-circle-outline' },
    ],
  }),
  async mounted () {
    const res = await this.$axios.get('/auth/fs/checkout/status')
    if (res && res.data) {
      this.inGrace = !!res.data.in_grace
      this.$store.dispatch('auth/set_fs_stripe_status', res.data.stripe_status || null)
      // If no access at all, redirect to paywall
      if (!res.data.can_access && !res.data.needs_checkout === false) {
        this.$router.push({ name: 'fs-paywall', query: { status: res.data.stripe_status || '' } })
      }
      if (res.data.needs_checkout) {
        this.$router.push({ name: 'fs-checkout' })
      }
    }
  },
  methods: {
    async openPortal () {
      this.portalLoading = true
      const res = await this.$axios.post('/auth/fs/checkout/portal')
      if (res && res.data && res.data.response) {
        window.location.href = res.data.url
      }
      this.portalLoading = false
    },
    isActive (to) {
      if (to === '/fs-dashboard') return this.$route.path === '/fs-dashboard'
      return this.$route.path.startsWith(to)
    },
    async signOut() {
      try {
        await this.$axios.post('/auth/logout')
      } catch (_) { /* ignore */ }
      this.$store.commit('auth/set_empty_data')
      this.$router.push('/')
    },
  },
}
</script>

<style scoped>
.fs-logo-circle {
  width: 40px; height: 40px;
  border-radius: 50%;
  background: #E0DFFB;
  color: #1A3A5C;
  font-weight: 700;
  font-size: 13px;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.fs-nav-link {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  padding: 8px 16px;
  color: #1A3A5C !important;
  text-decoration: none;
  gap: 4px;
}
.fs-nav-label {
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.01em;
}
.fs-nav-underline {
  position: absolute;
  bottom: -1px;
  left: 8px; right: 8px;
  height: 3px;
  background: #1A3A5C;
  opacity: 0;
  transition: opacity 0.15s;
}
.fs-nav-underline--active { opacity: 1; }
.fs-nav-link:hover .fs-nav-underline { opacity: 0.4; }
.fs-drawer-active {
  background: #E8EEF8 !important;
}
</style>

<!-- Global field styles for First Steps — non-scoped so they reach child components -->
<style>
/* Outlined text fields and selects: rounded, subtle blue border */
.v-application .v-input--is-focused.v-text-field--outlined > .v-input__control > .v-input__slot fieldset,
.v-application .v-input--is-focused.v-select--is-menu-active > .v-input__control > .v-input__slot fieldset {
  border-color: #2E75B6 !important;
  border-width: 1.5px !important;
}
.v-application .v-text-field--outlined > .v-input__control > .v-input__slot fieldset,
.v-application .v-select.v-text-field--outlined > .v-input__control > .v-input__slot fieldset {
  border-color: #B8CEDE !important;
  border-radius: 10px !important;
  border-width: 1.5px !important;
}
.v-application .v-text-field--outlined > .v-input__control > .v-input__slot,
.v-application .v-select.v-text-field--outlined > .v-input__control > .v-input__slot {
  border-radius: 10px !important;
  background: #fff !important;
}
</style>
